import { FilterX, Loader2 } from 'lucide-react';
import { useMemo, useState, useCallback } from 'react';

import { CustomersFilter } from './CustomersFilter';
import { CustomersTable } from './CustomersTable';

import { NoContentFallback } from '@/components/NoContentFallBack';
import { Pagination } from '@/components/Pagination';
import { SearchCustomersForm } from '@/components/SearchCustomers';
import { Card, CardContent } from '@/components/ui/Card';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/Select';
import { useGetPaginatedCustomers } from '@/hooks/useGetPaginatedCustomers';
import { Platform, Status } from '@/services/CustomerService';

interface Filters {
  searchTerm: string | null;
  platform: Platform | null;
  status: Status | null;
}

interface CustomerFilterParams {
  page: number;
  limit: number;
  searchTerm?: string;
  platform?: Platform;
  status?: Status;
}

export function CustomersView() {
  const [filters, setFilters] = useState<Filters>({
    searchTerm: null,
    platform: null,
    status: null,
  });
  const [page, setPage] = useState(1);
  const [perPage, setPerPage] = useState(10);

  // Estabiliza a função para evitar re-renders desnecessários
  const updateFilter = useCallback((partial: Partial<Filters>) => {
    setFilters((prev) => ({
      ...prev,
      ...partial,
    }));
    setPage(1);
  }, []);

  const customerFilter = useMemo(() => {
    const baseFilter: CustomerFilterParams = {
      page,
      limit: perPage,
      ...(filters.searchTerm !== null && { searchTerm: filters.searchTerm }),
      ...(filters.platform !== null && { platform: filters.platform }),
      ...(filters.status !== null && { status: filters.status }),
    };

    return baseFilter;
  }, [filters, page, perPage]);

  const { customers, totalPages, isLoading } =
    useGetPaginatedCustomers(customerFilter);

  const showCustomersTable = !isLoading && customers.length > 0;

  return (
    <div className="space-y-6">
      <Card>
        <CardContent className="p-4">
          <div className="flex flex-row items-end gap-4">
            <div className="w-full">
              <SearchCustomersForm
                onPageChange={setPage}
                onSearchChange={updateFilter}
              />
            </div>

            <div className="w-[30%] flex-shrink-0">
              <CustomersFilter
                onFiltersChange={updateFilter}
                filters={filters}
              />
            </div>

            <div>
              <Select
                value={perPage.toString()}
                onValueChange={(value) => {
                  setPerPage(Number(value));
                  setPage(1);
                }}
              >
                <SelectTrigger>
                  <SelectValue placeholder="Items per page" />
                </SelectTrigger>
                <SelectContent>
                  {[5, 10, 20, 50].map((n) => (
                    <SelectItem key={n} value={n.toString()}>
                      {n} per page
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          </div>
        </CardContent>
      </Card>

      {isLoading && (
        <div className="flex items-center justify-center py-4">
          <Loader2 className="h-6 w-6 animate-spin" />
        </div>
      )}

      {showCustomersTable && (
        <>
          <CustomersTable customers={customers} perPage={perPage} />
          <Pagination
            page={page}
            totalPages={totalPages}
            onPageChange={setPage}
            isLoading={isLoading}
          />
        </>
      )}

      {customers.length === 0 && !isLoading && (
        <NoContentFallback
          title="No results found"
          description="Try adjusting your filters or clearing them to see all customers."
          icon={FilterX}
        />
      )}
    </div>
  );
}
